import { useCallback, useEffect, useState } from "react";
import { container } from "tsyringe";
import Button, { EButtonVariant, EButtonColor, EButtonSize } from "components/elements/Button";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import UpcomingActionResponseResource from "common/resources/Project/Config/UpcomingAction/UpcomingActionResponseResource";
import { upsert } from "common/utils/array";
import { useProjectId } from "hooks/useProjectId";
import UpcomingActionService from "services/projects/UpcomingActionService";
import ActionCard from "./ActionCard";
import { EActionCardModal, useActionCardModal } from "./ActionCardModal";
import CreateActionModal from "./ActionCardModal/CreateActionModal";
import EditActionModal from "./ActionCardModal/EditActionModal";
import DeleteActionModal from "./ActionCardModal/DeleteActionModal";
import classes from "./classes.module.scss";

const upcomingActionService = container.resolve(UpcomingActionService);

export default function Actions() {
	const { actions, removeAction, upsertAction } = useActions();
	const { selectedAction, isModalOpen, onCloseModal, onOpenCreateModal, onOpenEditModal, onOpenDeleteModal } = useActionCardModal();

	const onCreated = useCallback(
		(action: UpcomingActionResponseResource) => {
			upsertAction(action);
			onCloseModal();
		},
		[upsertAction, onCloseModal],
	);

	const onEdited = useCallback(
		(action: UpcomingActionResponseResource) => {
			upsertAction(action);
			onCloseModal();
		},
		[upsertAction, onCloseModal],
	);

	const onDeleted = useCallback(
		(deletedAction: Pick<UpcomingActionResponseResource, "id">) => {
			removeAction(deletedAction);
			onCloseModal();
		},
		[onCloseModal, removeAction],
	);

	return (
		<div className={classes["root"]}>
			<div className={classes["title-container"]}>
				<Typography type="h3" weight="bold">
					{I18n.asset.pages.professional_space.tabs.actions.title}
				</Typography>
				<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} size={EButtonSize.SMALL} onClick={onOpenCreateModal}>
					{I18n.asset.pages.professional_space.tabs.actions.create_action}
				</Button>
			</div>

			<div className={classes["actions-container"]}>
				{actions.map((action) => (
					<ActionCard key={action.id} action={action} onEditClick={() => onOpenEditModal(action)} onDeleteClick={() => onOpenDeleteModal(action)} />
				))}
			</div>

			{isModalOpen(EActionCardModal.CREATE_ACTION) && <CreateActionModal isOpen onClose={onCloseModal} onCreated={onCreated} />}
			{isModalOpen(EActionCardModal.EDIT_ACTION) && selectedAction && <EditActionModal isOpen action={selectedAction} onClose={onCloseModal} onEdited={onEdited} />}
			{isModalOpen(EActionCardModal.DELETE_ACTION) && selectedAction && <DeleteActionModal isOpen action={selectedAction} onClose={onCloseModal} onDeleted={onDeleted} />}
		</div>
	);
}

const useActions = () => {
	const { projectId } = useProjectId();
	const [actions, setActions] = useState<UpcomingActionResponseResource[]>([]);

	const fetchActions = useCallback(() => {
		return upcomingActionService.getAllByProjectId(projectId).then(setActions);
	}, [projectId]);

	const upsertAction = useCallback((action: UpcomingActionResponseResource) => {
		setActions((actions) => upsert(actions, action, (a) => a.id === action.id));
	}, []);

	const removeAction = useCallback((deletedAction: Pick<UpcomingActionResponseResource, "id">) => {
		setActions((actions) => actions.filter((a) => a.id !== deletedAction.id));
	}, []);

	useEffect(() => {
		fetchActions();
	}, [fetchActions]);

	return { actions, removeAction, upsertAction };
};
